import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SportsSoccerRoundedIcon from "@mui/icons-material/SportsSoccerRounded";
import AssistantPhotoRoundedIcon from "@mui/icons-material/AssistantPhotoRounded";
import StyleRoundedIcon from "@mui/icons-material/StyleRounded";
import AppLayout from "../../components/ui/AppLayout";
import PageHeader from "../../components/ui/PageHeader";
import SectionCard from "../../components/ui/SectionCard";
import LoadingState from "../../components/ui/LoadingState";
import GameForm from "./GameForm";
import { cardColor } from "./GameCard";
import api from "../../api";

const toLocalInput = (value) => (value ? new Date(value).toISOString().substring(0, 16) : "");

const EventCard = ({ title, icon, items, empty, onDelete, render }) => (
  <SectionCard title={title} icon={icon}>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">{empty}</Typography>
    ) : (
      <Stack spacing={0.5}>
        {items.map((item, index) => (
          <Stack key={`${item.jogador?._id}-${index}`} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: "background.default" }}>
            <Typography variant="body2">{render(item)}</Typography>
            <Tooltip title="Excluir">
              <IconButton size="small" color="error" onClick={() => onDelete(item.jogador._id)}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    )}
  </SectionCard>
);

const EditarJogoPage = () => {
  const { jogoId } = useParams();
  const navigate = useNavigate();
  const [jogo, setJogo] = useState(null);
  const [times, setTimes] = useState([]);
  const [goleiros, setGoleiros] = useState([]);
  const [values, setValues] = useState({ time1: "", time2: "", goleiroTime1: "", goleiroTime2: "", dataJogo: "", placarTime1: 0, placarTime2: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const fetchData = async () => {
    try {
      const torneioResponse = await api.get("/getTorneioActive");
      const timesResponse = await api.get("/times");
      setTimes(timesResponse.data.times);
      setGoleiros(torneioResponse.data.goleiros || []);

      const jogoResponse = await api.get(`/jogos/${jogoId}`);
      const jogoData = jogoResponse.data.jogo;
      setJogo(jogoData);
      setValues({
        time1: jogoData.timeA._id,
        time2: jogoData.timeB._id,
        goleiroTime1: jogoData.goleiroTimeA || "",
        goleiroTime2: jogoData.goleiroTimeB || "",
        dataJogo: toLocalInput(jogoData.dataJogo),
        placarTime1: jogoData.placar.timeA,
        placarTime2: jogoData.placar.timeB,
      });
    } catch (error) {
      setSnackbar({ severity: "error", message: "Erro ao buscar dados do jogo" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jogoId]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.put(`/jogos`, {
        timeA: values.time1,
        timeB: values.time2,
        goleiroTime1: values.goleiroTime1,
        goleiroTime2: values.goleiroTime2,
        dataJogo: values.dataJogo,
        placar: { timeA: values.placarTime1, timeB: values.placarTime2 },
        jogoId,
      });
      navigate("/listar-jogos");
    } catch (error) {
      setSnackbar({ severity: "error", message: "Erro ao editar o jogo" });
    }
    setSaving(false);
  };

  const handleDelete = async (type, jogadorId) => {
    const endpoints = { gol: "/deleteGol", assistencia: "/deleteAssistencia", cartao: "/deleteCartao" };
    try {
      await api.post(endpoints[type], { jogoId, jogadorId });
      setSnackbar({ severity: "success", message: "Lançamento removido." });
      await fetchData();
    } catch (error) {
      setSnackbar({ severity: "error", message: `Erro ao remover ${type}` });
    }
  };

  if (loading) return <AppLayout><LoadingState /></AppLayout>;

  return (
    <AppLayout maxWidth="md">
      <PageHeader backTo="/listar-jogos" title="Editar jogo" subtitle={jogo ? `${jogo.timeA.nome} × ${jogo.timeB.nome}` : undefined} />
      <SectionCard sx={{ mb: 3 }}>
        <GameForm times={times} goleiros={goleiros} values={values} onChange={setValues} />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
          <Button color="inherit" onClick={() => navigate("/listar-jogos")}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>Salvar alterações</Button>
        </Stack>
      </SectionCard>
      {jogo && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <EventCard title="Gols" icon={<SportsSoccerRoundedIcon color="primary" />} items={jogo.gols} empty="Nenhum gol registrado." onDelete={(id) => handleDelete("gol", id)} render={(gol) => `${gol.jogador.nome} · ${gol.quantidade}`} />
          </Grid>
          <Grid item xs={12} md={4}>
            <EventCard title="Assistências" icon={<AssistantPhotoRoundedIcon color="primary" />} items={jogo.assistencias} empty="Nenhuma assistência registrada." onDelete={(id) => handleDelete("assistencia", id)} render={(a) => `${a.jogador.nome} · ${a.quantidade}`} />
          </Grid>
          <Grid item xs={12} md={4}>
            <EventCard
              title="Cartões"
              icon={<StyleRoundedIcon color="primary" />}
              items={jogo.cartoes}
              empty="Nenhum cartão registrado."
              onDelete={(id) => handleDelete("cartao", id)}
              render={(cartao) => (
                <Stack component="span" direction="row" spacing={0.75} alignItems="center">
                  <span style={{ width: 10, height: 14, borderRadius: 2, background: cardColor[cartao.tipo], display: "inline-block" }} />
                  <span>{cartao.jogador.nome}</span>
                </Stack>
              )}
            />
          </Grid>
        </Grid>
      )}
      <Snackbar open={Boolean(snackbar)} autoHideDuration={5000} onClose={() => setSnackbar(null)}>
        {snackbar ? <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default EditarJogoPage;
